// SCR-ORD-01..03 (frozen mockups-ord-v1): the Pre-orders page (Open · Delivered · Cancelled), the new pre-order sheet
// (no price, nothing reserved — ORD-01..03, ADR-119) and the cancel confirmation (ORD-05). The Storekeeper reads the page
// to prepare stock; a Seller sees the pre-orders assigned to them.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  EmptyState,
  Field,
  Input,
  LineAdd,
  List,
  ListRow,
  Notice,
  QuickActions,
  Select,
  SheetActions,
  SkeletonList,
  SummaryCard,
  Tabs,
} from '../../design/kit';
import {
  PRE_ORDERS,
  cancelPreOrder,
  createPreOrder,
  loadPreOrders,
  type PreOrder,
  type PreOrderLine,
} from '../../data/preorders';
import { loadUsers } from '../../data/settings';
import { useSession } from '../../app/session';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { businessToday, formatDate } from '../../lib/format';
import { messageKey } from '../../lib/errors';
import { refusalCode, useCatalogData, useCustomers, useTab } from '../catalog/common';
import { countWord } from '../stock/StockSheets';

const TABS = ['open', 'delivered', 'cancelled'] as const;
type Tab = (typeof TABS)[number];

const addDays = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};

function LineWords({ lines }: { lines: PreOrderLine[] }) {
  const { t } = useTranslation();
  return (
    <>
      {lines.map((l, i) => (
        <span key={`${l.product_id}-${i}`}>
          {i > 0 && <br />}
          <Name>{l.product}</Name> {countWord(t, l.unit, l.quantity)}
        </span>
      ))}
    </>
  );
}

export function PreOrdersPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { role, userId } = useSession();
  const [tab, setTab] = useTab(TABS, 'open');
  const [sheet, setSheet] = useState<'new' | PreOrder | null>(null);
  const [seller, setSeller] = useState('');
  const orders = useQuery({ queryKey: [...PRE_ORDERS, 'list'], queryFn: loadPreOrders });
  const users = useQuery({ queryKey: ['settings', 'users'], queryFn: loadUsers, enabled: role === 'owner' });
  const canWrite = role === 'owner' || role === 'seller';
  const all = (orders.data ?? []).filter((o) => !seller || o.seller_id === seller);
  const shown = all.filter((o) => o.status === tab);
  const open = all.filter((o) => o.status === 'open');
  const weekAgo = addDays(businessToday(), -7);
  const recent = (s: PreOrder['status']) =>
    all.filter((o) => o.status === s && (o.closed_at ?? '').slice(0, 10) >= weekAgo).length;
  return (
    <>
      <SummaryCard
        label={t('or.sum')}
        value={String(open.length)}
        figures={[
          {
            label: t('or.pieces'),
            value: countWord(
              t,
              'piece',
              open.reduce((s, o) => s + o.pieces, 0),
            ),
          },
          { label: t('or.delweek'), value: String(recent('delivered')) },
          { label: t('or.canweek'), value: String(recent('cancelled')) },
        ]}
      />
      {canWrite ? (
        <QuickActions
          actions={[{ id: 'new', label: t('act.newpre'), icon: 'plus', main: true, onSelect: () => setSheet('new') }]}
        />
      ) : (
        <Notice tone="info">{t('or.readonly')}</Notice>
      )}
      {role === 'owner' && (
        <Field label={t('of.seller')}>
          {({ id }) => (
            <Select id={id} value={seller} onChange={(e) => setSeller(e.target.value)}>
              <option value="">{t('or.allsellers')}</option>
              {(users.data ?? [])
                .filter((u) => u.role === 'seller' || u.role === 'owner')
                .map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.display_name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
      )}
      <Tabs
        label={t('p.orders')}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={[
          { id: 'open', label: t('tab.ordopen') },
          { id: 'delivered', label: t('tab.orddone') },
          { id: 'cancelled', label: t('tab.ordcancel') },
        ]}
      />
      {!orders.data ? (
        <SkeletonList label={t('loading')} />
      ) : shown.length === 0 ? (
        <EmptyState icon="box" title={t('or.empty')} text={t('or.empty.text')} />
      ) : (
        <List label={t(`tab.${tab === 'open' ? 'ordopen' : tab === 'delivered' ? 'orddone' : 'ordcancel'}`)}>
          {shown.map((o) => (
            <ListRow
              key={o.id}
              testId={`pre-${o.number}`}
              lead={{ initials: (o.seller_name ?? '?').slice(0, 2) }}
              title={<Name>{o.customer}</Name>}
              sub={
                <>
                  <Code>{o.number}</Code>
                  {role !== 'seller' && o.seller_name && <> · {t('or.by', { n: o.seller_name })}</>} ·{' '}
                  <bdi className="yv-nowrap">{t('or.wanted', { d: formatDate(o.wanted_date, lang) })}</bdi>
                  <br />
                  <LineWords lines={o.lines} />
                  {o.sale_number && (
                    <>
                      <br />
                      {t('or.linked', { n: '' })}
                      <Code>{o.sale_number}</Code>
                    </>
                  )}
                </>
              }
              badge={
                <Badge tone={o.status === 'open' ? 'info' : o.status === 'delivered' ? 'success' : 'neutral'}>
                  {t(o.status === 'open' ? 'or.open' : o.status === 'delivered' ? 'or.done' : 'or.cancelled')}
                </Badge>
              }
              actions={
                o.status === 'open' && (role === 'owner' || (role === 'seller' && o.seller_id === userId)) ? (
                  <Button pill onClick={() => setSheet(o)}>
                    {t('act.cancelpre')}
                  </Button>
                ) : undefined
              }
            />
          ))}
        </List>
      )}
      {sheet === 'new' && <PreOrderSheet onClose={() => setSheet(null)} />}
      {sheet && sheet !== 'new' && <CancelPreOrderSheet order={sheet} onClose={() => setSheet(null)} />}
    </>
  );
}

/** SCR-ORD-02: customer, Seller (the owner chooses; a Seller is fixed to themselves, ADR-119), wanted date, lines. */
function PreOrderSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { role, userId, name } = useSession();
  const qc = useQueryClient();
  const customers = useCustomers();
  const { products } = useCatalogData();
  const users = useQuery({ queryKey: ['settings', 'users'], queryFn: loadUsers, enabled: role === 'owner' });
  const [customer, setCustomer] = useState('');
  const [seller, setSeller] = useState(role === 'seller' ? userId : '');
  const [wanted, setWanted] = useState(addDays(businessToday(), 1));
  const [product, setProduct] = useState('');
  const [unit, setUnit] = useState<'box' | 'piece'>('piece');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<{ product: string; name: string; unit: 'box' | 'piece'; quantity: number }[]>([]);
  const [problem, setProblem] = useState('');
  const active = (products.data ?? []).filter((p) => p.active);
  const chosen = active.find((p) => p.id === product);
  const units = chosen
    ? ([chosen.sell_piece && 'piece', chosen.sell_box && 'box'].filter(Boolean) as ('box' | 'piece')[])
    : [];
  const save = useMutation({
    mutationFn: () =>
      createPreOrder({
        customer,
        seller,
        wanted,
        lines: lines.map(({ product: p, unit: u, quantity: q }) => ({ product: p, unit: u, quantity: q })),
        note,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRE_ORDERS });
      onClose();
    },
    onError: (e) => setProblem(t(messageKey(refusalCode(e)))),
  });
  const add = () => {
    const q = Number(quantity);
    if (!chosen || !Number.isInteger(q) || q <= 0) return setProblem(t('err.qtypos'));
    const u = units.includes(unit) ? unit : (units[0] ?? 'piece');
    setLines((ls) => [...ls, { product: chosen.id, name: chosen.name, unit: u, quantity: q }]);
    setProduct('');
    setQuantity('');
    setProblem('');
  };
  const submit = () => {
    if (!customer) return setProblem(t('err.nocust'));
    if (!seller) return setProblem(t('err.sellerreq'));
    if (lines.length === 0) return setProblem(t('err.noline'));
    setProblem('');
    save.mutate();
  };
  return (
    <BottomSheet title={t('act.newpre')} open onClose={onClose} busy={save.isPending}>
      <Field label={t('of.customer')}>
        {({ id }) => (
          <Select id={id} value={customer} onChange={(e) => setCustomer(e.target.value)}>
            <option value="">—</option>
            {(customers.data ?? [])
              .filter((c) => c.active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </Select>
        )}
      </Field>
      <Field label={t('of.seller')}>
        {({ id }) =>
          role === 'owner' ? (
            <Select id={id} value={seller} onChange={(e) => setSeller(e.target.value)}>
              <option value="">—</option>
              {(users.data ?? [])
                .filter((u) => (u.role === 'seller' || u.role === 'owner') && u.active)
                .map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.display_name}
                  </option>
                ))}
            </Select>
          ) : (
            <Input id={id} dir="auto" readOnly value={name} />
          )
        }
      </Field>
      <Field label={t('of.wanted')}>
        {({ id }) => (
          <Input
            id={id}
            type="date"
            dir="ltr"
            min={businessToday()}
            value={wanted}
            onChange={(e) => setWanted(e.target.value)}
          />
        )}
      </Field>
      <LineAdd>
        <Field label={t('product')}>
          {({ id }) => (
            <Select id={id} value={product} onChange={(e) => setProduct(e.target.value)}>
              <option value="">—</option>
              {active.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {units.length > 1 && (
          <Chips
            label={t('unit')}
            items={units.map((u) => ({ id: u, label: t(`unit.${u}`) }))}
            selected={[units.includes(unit) ? unit : (units[0] ?? 'piece')]}
            onToggle={(u) => setUnit(u as 'box' | 'piece')}
          />
        )}
        <Field label={t('quantity')}>
          {({ id }) => (
            <Input
              id={id}
              numeric
              inputMode="numeric"
              dir="ltr"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          )}
        </Field>
        <Button icon="plus" onClick={add}>
          {t('line.add')}
        </Button>
      </LineAdd>
      {lines.length > 0 && (
        <List label={t('act.newpre')} animateNew>
          {lines.map((l, i) => (
            <ListRow
              key={`${l.product}-${i}`}
              title={<Name>{l.name}</Name>}
              sub={countWord(t, l.unit, l.quantity)}
              actions={
                <Button
                  pill
                  aria-label={t('remove')}
                  icon="close"
                  onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                />
              }
            />
          ))}
        </List>
      )}
      <Field label={t('of.note')}>
        {({ id }) => <Input id={id} dir="auto" value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
      <p className="muted">{t('of.noprice')}</p>
      <Notice tone="info">{t('of.noreserve')}</Notice>
      {problem && <Notice tone="danger">{problem}</Notice>}
      <SheetActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button variant="main" busy={save.isPending} onClick={submit}>
          {t('of.save')}
        </Button>
      </SheetActions>
    </BottomSheet>
  );
}

/** SCR-ORD-03: cancel an open pre-order, no reason (ORD-05). */
function CancelPreOrderSheet({ order, onClose }: { order: PreOrder; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const qc = useQueryClient();
  const [problem, setProblem] = useState('');
  const cancel = useMutation({
    mutationFn: () => cancelPreOrder(order.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRE_ORDERS });
      onClose();
    },
    onError: (e) => setProblem(t(messageKey(refusalCode(e)))),
  });
  return (
    <BottomSheet title={`${t('oc.title')} · ${order.number}`} open onClose={onClose} busy={cancel.isPending}>
      <List label={order.number}>
        <ListRow
          title={<Name>{order.customer}</Name>}
          sub={
            <>
              <bdi className="yv-nowrap">{t('or.wanted', { d: formatDate(order.wanted_date, lang) })}</bdi>
              <br />
              <LineWords lines={order.lines} />
            </>
          }
        />
      </List>
      {problem && <Notice tone="danger">{problem}</Notice>}
      <SheetActions>
        <Button onClick={onClose}>{t('oc.keep')}</Button>
        <Button variant="danger" busy={cancel.isPending} onClick={() => cancel.mutate()}>
          {t('oc.confirm')}
        </Button>
      </SheetActions>
    </BottomSheet>
  );
}
